import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, Sparkles, Users, Copy, Play, ChevronRight, Trophy, Check, X } from "lucide-react";

const THEMES = ["Bourgogne", "Bordeaux", "Champagne", "Loire", "Rhône", "Languedoc", "Alsace", "Spiritueux"];

export default function PlayHosted() {
  const { user } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [theme, setTheme] = useState("Bourgogne");
  const [n, setN] = useState(5);
  const [busy, setBusy] = useState(false);
  const [room, setRoom] = useState(null); // { code, host_token, n_questions, theme }
  const [state, setState] = useState(null); // polled room state
  const pollRef = useRef(null);

  useEffect(() => { if (!user) navigate("/login?next=/play/hosted"); }, [user]); // eslint-disable-line

  useEffect(() => {
    if (!room) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/tlp/play/room/${room.code}`);
        setState(data);
        if (data.status === "ended" && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [room?.code]); // eslint-disable-line

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tlp/play/host", { theme, n_questions: n, host_name: user.name, lang });
      setRoom(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const next = async () => {
    try {
      const { data } = await api.post("/tlp/play/next", { code: room.code, host_token: room.host_token });
      if (data.status === "ended") toast.success("Partie terminée !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!user) return null;

  const cq = state?.current_question;
  const isLobby = state?.status === "lobby";
  const ended = state?.status === "ended";

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="play-hosted">
      <span className="chip-pink inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Play · Hosted</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Animez votre partie</h1>

      {!room && (
        <>
          <p className="text-[#4A5568] mt-3 max-w-2xl">geoVINUM AI génère un quiz sur-mesure, vous obtenez un code à 6 chiffres à partager — vos amis rejoignent sur leur téléphone.</p>
          <div className="mt-8 app-card p-6 space-y-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Thème</div>
              <div className="flex flex-wrap gap-2">{THEMES.map((t) => (
                <button key={t} onClick={() => setTheme(t)} data-testid={`theme-${t}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-bold border ${theme === t ? "bg-[#E5276A] text-white border-[#E5276A]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/12"}`}>
                  {t}
                </button>
              ))}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Nombre de questions</div>
              <input type="range" min="3" max="12" value={n} onChange={(e) => setN(parseInt(e.target.value))} data-testid="host-n-questions" className="w-full accent-[#E5276A]" />
              <div className="text-center text-sm text-[#1F3A5F] font-bold">{n} questions</div>
            </div>
            <button onClick={create} disabled={busy} data-testid="host-create"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Créer la partie (1 crédit)
            </button>
          </div>
        </>
      )}

      {room && state && (
        <div className="mt-8 space-y-6" data-testid="host-room">
          {/* Room header */}
          <div className="rounded-3xl bg-[#1F3A5F] text-white p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#FFD8E5] font-bold">Code à partager</div>
              <div className="font-display text-5xl tracking-widest font-bold mt-1" data-testid="host-code">{room.code}</div>
              <div className="text-xs text-white/70 mt-1">{state.theme} · {state.n_questions} questions</div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/play/join?code=${room.code}`); toast.success("Lien copié"); }}
                data-testid="host-copy" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#1F3A5F] text-sm font-bold hover:bg-[#FFD8E5]">
                <Copy className="w-4 h-4" /> Copier le lien
              </button>
              {!ended && (
                <button onClick={next} data-testid="host-next"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
                  <Play className="w-4 h-4" /> {isLobby ? "Démarrer" : state.current_idx + 1 < state.n_questions ? "Question suivante" : "Terminer"}
                </button>
              )}
            </div>
          </div>

          {/* Current question (host preview) */}
          {cq && !ended && (
            <div className="app-card p-6" data-testid="host-current-question">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Question {cq.idx + 1} / {state.n_questions}</div>
              <h3 className="font-display text-2xl text-[#1F3A5F]">{cq.question}</h3>
              <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {cq.choices.map((c, i) => (
                  <div key={i} className={`p-3 rounded-xl border text-sm ${i === cq.correct_idx ? "bg-green-50 border-green-300 text-green-900 font-bold" : "bg-white border-[#1F3A5F]/10 text-[#1F3A5F]"}`}>
                    {String.fromCharCode(65 + i)}. {c} {i === cq.correct_idx && "✓"}
                  </div>
                ))}
              </div>
              {cq.why && <p className="mt-3 text-xs text-[#8B95A1] italic">💡 {cq.why}</p>}
            </div>
          )}

          {/* Scoreboard / lobby */}
          <div className="app-card p-5" data-testid="host-scoreboard">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> {state.players.length} joueur(s) {isLobby && "· en attente"}
            </div>
            {state.scoreboard.length === 0 ? (
              <p className="text-sm text-[#8B95A1] italic">Aucun joueur n'a encore rejoint.</p>
            ) : (
              <ul className="space-y-1.5">
                {state.scoreboard.map((s, i) => (
                  <li key={s.player_id} className="flex items-center justify-between p-2 rounded-lg bg-[#FBF7F3]">
                    <span className="text-sm font-bold text-[#1F3A5F]">{i === 0 && ended ? "🏆 " : `${i + 1}. `}{s.name}</span>
                    <span className="text-xs text-[#4A5568]">{s.score} / {state.n_questions} · {s.answered} répondues</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {ended && (
            <div className="rounded-3xl bg-gradient-to-br from-[#FFF8FB] to-[#FBF7F3] border-2 border-[#E5276A]/30 p-8 text-center" data-testid="host-ended">
              <Trophy className="w-12 h-12 text-[#E5276A] mx-auto" />
              <h2 className="font-display text-3xl text-[#1F3A5F] mt-3">Partie terminée 🍷</h2>
              {state.scoreboard[0] && <p className="text-base text-[#4A5568] mt-2">Gagnant : <b className="text-[#1F3A5F]">{state.scoreboard[0].name}</b> avec {state.scoreboard[0].score}/{state.n_questions}.</p>}
              <button onClick={() => { setRoom(null); setState(null); }} className="mt-5 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
                Nouvelle partie
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 text-center">
        <Link to="/play/join" className="text-xs text-[#8B95A1] hover:text-[#E5276A] underline">Rejoindre une partie avec un code</Link>
      </div>
    </div>
  );
}
